<template>
  <system-frame class="st" title="Statistika" hint="Töökäsud ja töötunnid tehnikute, töökäsu ja objekti tüübi, makseviisi, päeva liigi ja planeerija päeva järgi. Kustutatud töökäske ei loeta.">
    <!-- Süsteem -> Statistika (root), agreed on a mockup first. The server
         gives one small row per töökäsk of the period (service/stats.js) and
         everything here is counted from those, so a filter answers at once;
         the list behind a number is asked for with the same filters. -->
    <div class="st-card st-filters">
      <div class="st-frow">
        <span class="st-k">Periood</span>
        <div class="st-chips">
          <div class="st-seg" role="group" aria-label="Perioodi pikkus">
            <button v-for="item in MODES" :key="item.value" type="button" :aria-pressed="String(mode === item.value)" @click="setMode(item.value)">{{ item.text }}</button>
          </div>
          <template v-if="mode !== 'custom'">
            <button type="button" class="st-icon" aria-label="Eelmine periood" @click="move(-1)">‹</button>
            <span class="st-period">{{ periodLabel }}</span>
            <button type="button" class="st-icon" aria-label="Järgmine periood" @click="move(1)">›</button>
          </template>
          <template v-else>
            <date-field v-model="from" label="Algus" density="compact" variant="outlined" hide-details style="max-width: 170px" />
            <date-field v-model="to" label="Lõpp" density="compact" variant="outlined" hide-details style="max-width: 170px" />
          </template>
          <span v-if="running" class="st-muted">Periood kestab: loetud on tänaseni, ees ootavaid töökäske ei loeta.</span>
        </div>
      </div>
      <div class="st-frow">
        <span class="st-k">Võrdle</span>
        <div class="st-chips">
          <div class="st-seg" role="group" aria-label="Võrdlus">
            <button v-for="item in COMPARE" :key="item.value" type="button" :aria-pressed="String(compare === item.value)" @click="compare = item.value">{{ item.text }}</button>
          </div>
          <span v-if="cmpRange" class="st-muted">võrdlus: {{ dmy(cmpRange[0]) }} – {{ dmy(cmpRange[1]) }}<template v-if="running"> (sama arv päevi)</template></span>
        </div>
      </div>
      <div v-for="filter in filters" :key="filter.key" class="st-frow">
        <span class="st-k">{{ filter.label }}</span>
        <div class="st-chips">
          <button
            v-for="item in filter.items"
            :key="item.id"
            type="button"
            class="st-chip"
            :aria-pressed="String(isOn(filter.key, item.id))"
            :title="item.title || null"
            @click="toggle(filter, item.id)"
          >{{ item.text }}</button>
          <button v-if="filter.items.length" type="button" class="st-link" @click="setAll(filter, !allOn(filter))">{{ allOn(filter) ? 'Tühjenda' : 'Kõik' }}</button>
          <span v-else class="st-muted">Sel perioodil töökäske ei ole.</span>
        </div>
      </div>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="green" height="3" />

    <div class="st-tiles">
      <button
        v-for="tile in tiles"
        :key="tile.key"
        type="button"
        class="st-card st-tile"
        :class="{ 'st-tile--plain': !tile.list }"
        :disabled="!tile.list"
        :aria-pressed="tile.list ? String(!!list && list.kind === tile.list) : null"
        :title="tile.list ? 'Näita neid töökäske' : null"
        @click="tile.list && openList({ kind: tile.list })"
      >
        <span class="st-lab">{{ tile.label }}<template v-if="tile.list"> ›</template></span>
        <span class="st-num">{{ tile.value }}</span>
        <span class="st-sub"><b v-if="tile.delta" class="st-delta">{{ tile.delta }}</b> {{ tile.sub }}</span>
      </button>
    </div>

    <div v-if="list" ref="list" class="st-card st-sect">
      <div class="st-sect-head">
        <h2>{{ listTitle }} ({{ num(listCount) }})</h2>
        <span class="st-muted">Samad filtrid mis üleval. Kuupäev avab töökäsu.</span>
        <button type="button" class="st-out" @click="list = null">Sulge</button>
      </div>
      <v-progress-linear v-if="listLoading" indeterminate color="green" height="3" />
      <div class="st-scroll">
        <table class="st-table st-table--list">
          <thead>
            <tr><th>Kuupäev</th><th>Päev</th><th>Tehnik</th><th>Tüüp</th><th>Kestus</th><th>Makseviis</th><th>Planeerijas</th><th>Objekt</th><th>Klient</th></tr>
          </thead>
          <tbody>
            <tr v-for="entry in entries" :key="entry.id">
              <td><button type="button" class="st-numlink" title="Ava töökäsk" @click="openEvent(entry)">{{ dmy(entry.date) }} {{ time(entry.startDate) }}</button></td>
              <td>{{ DAYS[entry.dayKind] }}</td>
              <td>{{ technicName(entry.userId) }}<template v-if="entry.helperId">, {{ technicName(entry.helperId) }}</template></td>
              <td>{{ TYPES[entry.category] || 'Muu' }}</td>
              <td>{{ entry.minutes ? hours1(entry.minutes) + ' h' : '–' }}</td>
              <td>{{ PAYMENTS[entry.paymentType] }}</td>
              <td>{{ workdayName(entry.workdayId) || '–' }}</td>
              <td>
                <router-link v-if="entry.objectId" :to="{ name: 'object/view/info', params: { objectId: entry.objectId } }">{{ entry.address || 'Objekt' }}</router-link><span v-if="entry.objectType === 1" class="st-muted"> · lepinguline</span>
                <template v-else>–</template>
              </td>
              <td>
                <router-link v-if="entry.clientId" :to="{ name: 'client/view', params: { clientId: entry.clientId } }">{{ entry.clientName || 'Klient' }}</router-link>
                <template v-else>–</template>
              </td>
            </tr>
            <tr v-if="!entries.length && !listLoading"><td colspan="9" class="st-muted">Selle valikuga töökäske ei ole.</td></tr>
          </tbody>
        </table>
      </div>
      <button v-if="entries.length < listCount" type="button" class="st-link st-more" :disabled="listLoading" @click="loadList(true)">Näita veel ({{ num(listCount - entries.length) }})</button>
    </div>

    <div class="st-card st-sect">
      <div class="st-sect-head">
        <h2>{{ chartTitle }}</h2>
        <div class="st-legend">
          <span><i class="st-now" />Valitud periood</span>
          <span v-if="cmpRange"><i class="st-was" />Võrdlus</span>
        </div>
        <div class="st-seg" role="group" aria-label="Mida näidata">
          <button v-for="item in MEASURES" :key="item.value" type="button" :aria-pressed="String(measure === item.value)" @click="measure = item.value">{{ item.text }}</button>
        </div>
      </div>
      <div class="st-scroll">
        <div class="st-chart" @pointerleave="hover = null">
          <svg :viewBox="'0 0 ' + chart.W + ' ' + chart.H" width="100%" role="img" :aria-label="chartTitle">
            <g v-for="line in chart.grid" :key="line.y">
              <line :x1="chart.L" :x2="chart.W - chart.R" :y1="line.y" :y2="line.y" :stroke="line.base ? '#C9D2CE' : '#E1E7E4'" stroke-width="1" />
              <text :x="chart.L - 8" :y="line.y + 4" text-anchor="end">{{ line.text }}</text>
            </g>
            <g v-for="(bar, i) in chart.bars" :key="bar.key">
              <rect v-if="bar.weekend" :x="bar.x" :y="chart.T" :width="chart.step" :height="chart.plot" fill="#EEF1EF" />
              <path v-if="bar.was" :d="bar.was" fill="#B9C4BF" />
              <path v-if="bar.now" :d="bar.now" fill="#2E7D32" />
              <text v-if="bar.label" :x="bar.cx" :y="chart.H - 8" text-anchor="middle">{{ bar.label }}</text>
              <rect :x="bar.x" :y="chart.T" :width="chart.step" :height="chart.plot" fill="transparent" @pointerenter="hover = i" />
            </g>
          </svg>
          <div v-if="tip" class="st-tip" :style="tip.style">
            {{ tip.when }}: <b>{{ tip.now }}</b>
            <template v-if="tip.was != null"><br>võrdlus<template v-if="tip.wasWhen"> ({{ tip.wasWhen }})</template>: {{ tip.was }}</template>
          </div>
        </div>
      </div>
    </div>

    <div class="st-card st-sect">
      <div class="st-sect-head">
        <h2>Tehnikute kaupa</h2>
        <span class="st-muted">Vajuta veeru pealkirjal, et sorteerida. Arv avab töökäsud.</span>
        <button type="button" class="st-out" :disabled="!perTechnic.length" @click="exportCsv()">Ekspordi CSV</button>
      </div>
      <div class="st-scroll">
        <table class="st-table">
          <thead>
            <tr>
              <th
                v-for="col in COLUMNS"
                :key="col.key"
                :aria-sort="sort === col.key ? (dir > 0 ? 'ascending' : 'descending') : null"
                @click="sortBy(col.key)"
              >{{ col.text }}<template v-if="sort === col.key">{{ dir > 0 ? ' ↑' : ' ↓' }}</template></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in perTechnic" :key="row.id">
              <td>{{ row.name }}</td>
              <td>
                <span class="st-bar" :style="{ width: Math.round(row.n / maxTechnic * 70) + 'px' }" />
                <button type="button" class="st-numlink" title="Näita neid töökäske" @click="openList({ kind: 'tech', id: row.id })">{{ num(row.n) }}</button>
              </td>
              <td><b v-if="cmpRange" class="st-delta">{{ delta(row.n, row.was) }}</b><span v-else class="st-muted">–</span></td>
              <td>{{ hours(row.m) }}</td>
              <td>{{ dash(row.wd) }}</td>
              <td>{{ dash(row.we) }}</td>
              <td>{{ dash(row.ho) }}</td>
              <td>{{ dash(row.fr) }}</td>
            </tr>
            <tr v-if="!perTechnic.length"><td colspan="8" class="st-muted">Selle valikuga töökäske ei ole.</td></tr>
          </tbody>
          <tfoot v-if="perTechnic.length">
            <tr>
              <td>Kokku ({{ perTechnic.length }})</td>
              <td>{{ num(total.n) }}</td>
              <td>{{ cmpRange ? delta(total.n, totalWas.n) : '' }}</td>
              <td>{{ hours(total.m) }}</td>
              <td>{{ num(total.wd) }}</td>
              <td>{{ num(total.we) }}</td>
              <td>{{ num(total.ho) }}</td>
              <td>{{ num(total.fr) }}</td>
            </tr>
          </tfoot>
        </table>
        <p v-if="hasShared" class="st-muted">Kahe tehnikuga töökäsk on kummagi tehniku real, kokkuvõttes üks kord, nii et read võivad anda kokku rohkem.</p>
      </div>
    </div>

    <div class="st-two">
      <div class="st-card st-sect">
        <h2>Tüübi kaupa</h2>
        <div class="st-scroll">
          <table class="st-table st-table--narrow">
            <thead><tr><th>Tüüp</th><th>Töökäske</th><th>Osakaal</th><th>Tunde</th></tr></thead>
            <tbody>
              <tr v-for="row in perType" :key="row.id">
                <td>{{ row.name }}</td>
                <td><span class="st-bar" :style="{ width: Math.round(row.n / maxType * 60) + 'px' }" />{{ num(row.n) }}</td>
                <td>{{ Math.round(row.n / total.n * 100) }}%</td>
                <td>{{ hours(row.m) }}</td>
              </tr>
              <tr v-if="!perType.length"><td colspan="4" class="st-muted">Selle valikuga töökäske ei ole.</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="st-card st-sect">
        <h2>Makseviis</h2>
        <div class="st-pay">
          <div v-for="item in perPayment" :key="item.id"><span class="st-muted">{{ item.name }}</span><b>{{ num(item.n) }}</b><span class="st-muted">{{ item.share }}%</span></div>
        </div>
        <span class="st-muted">Töökäskude arv makseviisi järgi.</span>
      </div>
    </div>

    <v-dialog v-model="eventOpen" :max-width="features && features.eventPlate ? 880 : 700">
      <event v-if="eventOpen" mode="view" :eventId="eventId" @close="closeEvent" @updated="eventChanged = true" @deleted="eventChanged = true" />
    </v-dialog>
  </system-frame>
</template>

<script>
import SystemFrame from '@/components/SystemFrame.vue'
import DateField from '@/components/input/DateField.vue'
import api from '@/api'
import dayjs from 'dayjs'
import loadArchivo from '@/plugins/archivo'
import { mapActions, mapState } from 'vuex'

// As server/models/calendar.js names them.
const TYPES = ['Hooldus', 'Valve', 'Automaatika', 'Garantii', 'Muu', 'Lepinguline', 'Käivitamine']
const PAYMENTS = ['Maksmata', 'Arvega', 'Sularaha', 'Tasuta']
// service/stats.js DAY: a holiday on a weekend is a holiday.
const DAYS = ['Tööpäev', 'Nädalavahetus', 'Riigipüha']
// server/models/objectModel.js types; the object's type as it is today.
const OBJECTS = ['Tavaline', 'Lepinguline']
// Planeerija day kinds (workday.title) that mean the technician was off.
const FREE = ['vaba', 'puhkus']
const MODES = [{ value: 'month', text: 'Kuu' }, { value: 'quarter', text: 'Kvartal' }, { value: 'year', text: 'Aasta' }, { value: 'custom', text: 'Oma vahemik' }]
const COMPARE = [{ value: 'prev', text: 'Eelmise perioodiga' }, { value: 'yoy', text: 'Aasta varem' }, { value: 'none', text: 'Ei võrdle' }]
const MEASURES = [{ value: 'n', text: 'Töökäsud' }, { value: 'h', text: 'Tunnid' }]
const COLUMNS = [
  { key: 'name', text: 'Tehnik' }, { key: 'n', text: 'Töökäske' }, { key: 'd', text: 'Muutus' }, { key: 'm', text: 'Tunde' },
  { key: 'wd', text: 'Tööpäev' }, { key: 'we', text: 'Nädalavah.' }, { key: 'ho', text: 'Riigipüha' }, { key: 'fr', text: 'Vabal päeval / puhkusel' }
]
const MONTHS = ['jaan', 'veebr', 'märts', 'apr', 'mai', 'juuni', 'juuli', 'aug', 'sept', 'okt', 'nov', 'dets']
const YMD = 'YYYY-MM-DD'
const PAGE = 50

// Days since 1970, so a week or a month of rows buckets without a date
// object per row. Day 0 was a Thursday.
const dayIndex = ymd => Math.round(Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10)) / 864e5)
const ymdOf = index => new Date(index * 864e5).toISOString().slice(0, 10)
const num = n => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

export default {
  components: { SystemFrame, DateField },
  data () {
    const month = dayjs().startOf('month')
    return {
      MODES, COMPARE, MEASURES, COLUMNS, TYPES, PAYMENTS, DAYS,
      mode: 'month',
      // The first day of the month, quarter or year on screen.
      anchor: month.format(YMD),
      from: month.format(YMD),
      to: month.endOf('month').format(YMD),
      compare: 'prev',
      measure: 'n',
      // A filter: null lets everything through, else the ids picked.
      picked: { tech: null, type: null, obj: null, pay: null, day: null, plan: null },
      sort: 'n',
      dir: -1,
      loading: false,
      rows: [],
      rowsWas: [],
      technics: [],
      workdays: [],
      hover: null,
      // The töökäsud behind a number: { kind: 'all' | 'off' | 'free' | 'tech', id }
      list: null,
      entries: [],
      listCount: 0,
      listLoading: false,
      eventOpen: false,
      eventId: null,
      eventChanged: false
    }
  },
  computed: {
    ...mapState(['features']),
    today: () => dayjs().format(YMD),
    range () {
      const start = dayjs(this.anchor)
      if (this.mode === 'month') return [start.format(YMD), start.endOf('month').format(YMD)]
      if (this.mode === 'quarter') return [start.format(YMD), start.add(2, 'month').endOf('month').format(YMD)]
      if (this.mode === 'year') return [start.format(YMD), start.endOf('year').format(YMD)]
      return this.from <= this.to ? [this.from, this.to] : [this.to, this.from]
    },
    // Today falls inside the period: what is still ahead is planned, not
    // done, so the period is counted up to today and compared with as many
    // days of the other one.
    running () {
      return this.range[0] <= this.today && this.range[1] > this.today
    },
    // The days that are counted.
    span () {
      return this.running ? [this.range[0], this.today] : this.range
    },
    cmpRange () {
      if (this.compare === 'none') return null
      const [from, to] = this.range.map(day => dayjs(day))
      let range
      if (this.compare === 'yoy') range = [from.subtract(1, 'year'), to.subtract(1, 'year')]
      else if (this.mode === 'month') range = [from.subtract(1, 'month'), from.subtract(1, 'day')]
      else if (this.mode === 'quarter') range = [from.subtract(3, 'month'), from.subtract(1, 'day')]
      else if (this.mode === 'year') range = [from.subtract(1, 'year'), from.subtract(1, 'day')]
      else range = [from.subtract(to.diff(from, 'day') + 1, 'day'), from.subtract(1, 'day')]
      if (this.running) {
        const until = range[0].add(dayjs(this.today).diff(from, 'day'), 'day')
        if (until.isBefore(range[1])) range[1] = until
      }
      return range.map(day => day.format(YMD))
    },
    periodLabel () {
      const start = dayjs(this.range[0])
      if (this.mode === 'month') return this.capital(start.format('MMMM YYYY'))
      if (this.mode === 'quarter') return (Math.floor(start.month() / 3) + 1) + '. kvartal ' + start.year()
      return String(start.year())
    },
    freeIds () {
      return this.workdays.filter(workday => FREE.includes(workday.title)).map(workday => workday.id)
    },
    // Only the technicians who have a töökäsk in either period.
    filters () {
      return [
        { key: 'tech', label: 'Tehnikud', items: this.technics.map(technic => ({ id: technic.id, text: technic.fullName, title: technic.active ? null : 'Mitteaktiivne kasutaja' })) },
        { key: 'type', label: 'Töökäsu tüüp', items: TYPES.map((text, id) => ({ id, text })) },
        { key: 'obj', label: 'Objekti tüüp', items: OBJECTS.map((text, id) => ({ id, text })) },
        { key: 'pay', label: 'Makseviis', items: [1, 2, 3, 0].map(id => ({ id, text: PAYMENTS[id] })) },
        { key: 'day', label: 'Päev', items: DAYS.map((text, id) => ({ id, text })) },
        { key: 'plan', label: 'Planeerijas', items: [{ id: 0, text: 'Märkimata' }].concat(this.workdays.map(workday => ({ id: workday.id, text: workday.name }))) }
      ]
    },
    shown () {
      return this.rows.filter(this.passes)
    },
    shownWas () {
      return this.cmpRange ? this.rowsWas.filter(this.passes) : []
    },
    total () {
      return this.sum(this.shown)
    },
    totalWas () {
      return this.sum(this.shownWas)
    },
    tiles () {
      const { total, totalWas } = this
      const cmp = !!this.cmpRange
      const off = total.we + total.ho
      return [{
        key: 'n', label: 'Töökäske', value: num(total.n), list: 'all',
        delta: cmp ? this.delta(total.n, totalWas.n) : '',
        sub: cmp ? 'oli ' + num(totalWas.n) : this.dmy(this.span[0]) + ' – ' + this.dmy(this.span[1])
      }, {
        key: 'h', label: 'Tunde', value: this.hours(total.m),
        delta: cmp ? this.delta(total.m, totalWas.m) : '',
        sub: total.n ? 'keskmiselt ' + this.hours1(total.m / total.n) + ' h töökäsu kohta' : ''
      }, {
        key: 'off', label: 'Nädalavahetusel ja pühal', value: num(off), list: 'off',
        delta: cmp ? this.delta(off, totalWas.we + totalWas.ho) : '',
        sub: total.n ? Math.round(off / total.n * 100) + '% töökäskudest' : ''
      }, {
        key: 'free', label: 'Vabal päeval ja puhkusel', value: num(total.fr), list: 'free',
        delta: cmp ? this.delta(total.fr, totalWas.fr) : '',
        sub: 'planeerijas Vaba päev või Puhkusel'
      }]
    },
    perTechnic () {
      // A shared töökäsk counts for both technicians, once in the total.
      const { tech } = this.picked
      const people = row => [row.u, row.h].filter(id => id && (!tech || tech.includes(id)))
      const was = {}
      for (const row of this.shownWas) for (const id of people(row)) was[id] = (was[id] || 0) + 1
      const groups = {}
      for (const row of this.shown) for (const id of people(row)) (groups[id] = groups[id] || []).push(row)
      const list = this.technics.filter(technic => groups[technic.id] || was[technic.id]).map(technic => {
        const before = was[technic.id] || 0
        const sum = this.sum(groups[technic.id] || [])
        return { id: technic.id, name: technic.fullName, ...sum, was: before, d: before ? (sum.n - before) / before : (sum.n ? Infinity : 0) }
      })
      const { sort, dir } = this
      return list.sort((a, b) => sort === 'name' ? dir * a.name.localeCompare(b.name, 'et') : dir * (a[sort] - b[sort]) || a.name.localeCompare(b.name, 'et'))
    },
    hasShared () {
      return this.shown.some(row => row.h)
    },
    maxTechnic () {
      return Math.max(1, ...this.perTechnic.map(row => row.n))
    },
    perType () {
      return TYPES.map((name, id) => ({ id, name, ...this.sum(this.shown.filter(row => row.k === id)) }))
        .filter(row => row.n).sort((a, b) => b.n - a.n)
    },
    maxType () {
      return Math.max(1, ...this.perType.map(row => row.n))
    },
    perPayment () {
      return [1, 2, 3, 0].map(id => {
        const n = this.shown.filter(row => row.pt === id).length
        return { id, name: PAYMENTS[id], n, share: this.total.n ? Math.round(n / this.total.n * 100) : 0 }
      })
    },
    bucketKind () {
      const span = dayIndex(this.range[1]) - dayIndex(this.range[0]) + 1
      return span <= 31 ? 'day' : span <= 124 ? 'week' : 'month'
    },
    chartTitle () {
      return (this.measure === 'n' ? 'Töökäsud ' : 'Töötunnid ') + { day: 'päevade', week: 'nädalate', month: 'kuude' }[this.bucketKind] + ' kaupa'
    },
    // One scale for both periods; the comparison's buckets sit beside the
    // period's in the same order (its first week beside this first week).
    chart () {
      const now = this.fill(this.buckets(this.range), this.shown)
      const was = this.cmpRange ? this.fill(this.buckets(this.cmpRange), this.shownWas) : null
      const W = 1180, H = 250, L = 46, R = 8, T = 10, B = 26
      const plot = H - T - B
      const count = now.length || 1
      const top = this.niceTop(Math.max(0, ...now.map(b => b.v), ...(was ? was.slice(0, count).map(b => b.v) : [])))
      const y = value => T + plot * (1 - value / top)
      const step = (W - L - R) / count
      const width = Math.max(3, Math.min(26, (step - 4) / (was ? 2 : 1) - 1))
      const bar = (value, x) => value > 0
        ? 'M' + x + ' ' + y(0) + 'V' + (y(value) + Math.min(3, y(0) - y(value))) + 'q0 -3 3 -3h' + Math.max(0, width - 6) + 'q3 0 3 3V' + y(0) + 'z'
        : null
      const every = Math.ceil(count / 32)
      return {
        W, H, L, R, T, plot, step,
        grid: [0, 1, 2, 3, 4].map(i => ({ y: y(top * i / 4), text: this.axis(top * i / 4), base: i === 0 })),
        bars: now.map((bucket, i) => {
          const cx = L + step * i + step / 2
          const other = was && was[i]
          return {
            key: bucket.key,
            x: L + step * i,
            cx,
            weekend: bucket.weekend,
            label: i % every === 0 ? bucket.label : '',
            now: bar(bucket.v, was ? cx + 1 : cx - width / 2),
            was: was ? bar(other ? other.v : 0, cx - width - 1) : null,
            bucket,
            other,
            top: y(Math.max(bucket.v, other ? other.v : 0)) / H
          }
        })
      }
    },
    tip () {
      const bar = this.hover == null ? null : this.chart.bars[this.hover]
      if (!bar) return null
      const unit = this.measure === 'n' ? '' : ' h'
      const when = bucket => bucket.from === bucket.to ? this.dmy(ymdOf(bucket.from)) : this.dmy(ymdOf(bucket.from)) + ' – ' + this.dmy(ymdOf(bucket.to))
      const left = Math.min(Math.max(bar.cx / this.chart.W * 100, 8), 92)
      return {
        when: when(bar.bucket),
        now: num(bar.bucket.v) + unit,
        was: this.cmpRange ? (bar.other ? num(bar.other.v) + unit : '–') : null,
        wasWhen: bar.other ? when(bar.other) : '',
        style: { left: left + '%', top: 'calc(' + (bar.top * 100) + '% - 6px)' }
      }
    },
    listTitle () {
      const list = this.list
      if (!list) return ''
      if (list.kind === 'off') return 'Töökäsud nädalavahetusel ja pühal'
      if (list.kind === 'free') return 'Töökäsud vabal päeval ja puhkusel'
      if (list.kind === 'tech') return 'Töökäsud: ' + this.technicName(list.id)
      return 'Töökäsud'
    },
    // What the open list asks the server for: the filters above, narrowed by
    // the number that was pressed. Null when nothing is open.
    listQuery () {
      if (!this.list) return null
      const both = (picked, only) => picked ? picked.filter(id => only.includes(id)) : only
      const { tech, type, obj, pay, day, plan } = this.picked
      const query = { startDate: this.span[0], endDate: this.span[1] }
      const set = (key, ids) => { if (ids) query[key] = ids.join(',') }
      set('technicIds', this.list.kind === 'tech' ? both(tech, [this.list.id]) : tech)
      set('categories', type)
      set('objectTypes', obj)
      set('paymentTypes', pay)
      set('dayKinds', this.list.kind === 'off' ? both(day, [1, 2]) : day)
      set('workdayIds', this.list.kind === 'free' ? both(plan, this.freeIds) : plan)
      return query
    }
  },
  watch: {
    span: {
      handler () {
        this.load()
      },
      immediate: true
    },
    cmpRange () {
      this.load()
    },
    listQuery () {
      this.loadList(false)
    }
  },
  created () {
    // The page is drawn in the plate look whatever the Süsteem switch says.
    loadArchivo()
  },
  methods: {
    ...mapActions(['showNotification']),
    num,
    dmy: ymd => ymd.slice(8, 10) + '.' + ymd.slice(5, 7) + '.' + ymd.slice(0, 4),
    time: value => value ? dayjs(value).format('HH:mm') : '',
    hours: minutes => num(minutes / 60),
    hours1: minutes => (minutes / 60).toFixed(1).replace('.', ','),
    dash: n => n ? num(n) : '–',
    capital: text => text.charAt(0).toUpperCase() + text.slice(1),
    axis: value => value < 10 && value % 1 ? value.toFixed(1).replace('.', ',') : num(value),
    technicName (id) {
      const technic = this.technics.find(technic => technic.id === id)
      return technic ? technic.fullName : '–'
    },
    workdayName (id) {
      const workday = this.workdays.find(workday => workday.id === id)
      return workday ? workday.name : ''
    },
    delta (now, was) {
      if (!was) return now ? 'uus' : '–'
      const percent = Math.round((now - was) / was * 100)
      return (percent > 0 ? '▲ +' : percent < 0 ? '▼ −' : '= ') + Math.abs(percent) + '%'
    },
    sum (rows) {
      const sum = { n: 0, m: 0, wd: 0, we: 0, ho: 0, fr: 0 }
      const free = this.freeIds
      for (const row of rows) {
        sum.n++
        sum.m += row.m
        if (row.dk === 0) sum.wd++
        else if (row.dk === 1) sum.we++
        else sum.ho++
        if (free.includes(row.w)) sum.fr++
      }
      return sum
    },
    passes (row) {
      const { tech, type, obj, pay, day, plan } = this.picked
      return (!tech || tech.includes(row.u) || tech.includes(row.h)) && (!type || type.includes(row.k)) && (!obj || obj.includes(row.ot)) &&
        (!pay || pay.includes(row.pt)) &&
        (!day || day.includes(row.dk)) && (!plan || plan.includes(row.w))
    },
    isOn (key, id) {
      const picked = this.picked[key]
      return !picked || picked.includes(id)
    },
    allOn (filter) {
      const picked = this.picked[filter.key]
      return !picked || filter.items.every(item => picked.includes(item.id))
    },
    toggle (filter, id) {
      const picked = this.picked[filter.key] || filter.items.map(item => item.id)
      const next = picked.includes(id) ? picked.filter(other => other !== id) : picked.concat(id)
      // Everything picked again is "no filter": a technician who turns up in
      // another period is then counted too.
      this.picked[filter.key] = filter.items.every(item => next.includes(item.id)) ? null : next
    },
    setAll (filter, on) {
      this.picked[filter.key] = on ? null : []
    },
    setMode (mode) {
      if (mode === this.mode) return
      const [from, to] = this.range
      if (mode === 'custom') {
        this.from = from
        this.to = to
      } else {
        // Keep looking at the same time: the month, quarter or year of the start.
        const start = dayjs(from)
        this.anchor = (mode === 'year' ? start.startOf('year') : mode === 'quarter' ? start.month(Math.floor(start.month() / 3) * 3).startOf('month') : start.startOf('month')).format(YMD)
      }
      this.mode = mode
    },
    move (step) {
      const unit = this.mode === 'year' ? 'year' : 'month'
      this.anchor = dayjs(this.anchor).add(step * (this.mode === 'quarter' ? 3 : 1), unit).format(YMD)
    },
    sortBy (key) {
      if (this.sort === key) this.dir = -this.dir
      else {
        this.sort = key
        this.dir = key === 'name' ? 1 : -1
      }
    },
    async load () {
      const asked = this.span.join('/') + '|' + (this.cmpRange || []).join('/')
      this.loading = true
      try {
        const [now, was] = await Promise.all([
          api.getStats({ startDate: this.span[0], endDate: this.span[1] }),
          this.cmpRange ? api.getStats({ startDate: this.cmpRange[0], endDate: this.cmpRange[1] }) : null
        ])
        // A later click already asked for something else.
        if (asked !== this.span.join('/') + '|' + (this.cmpRange || []).join('/')) return
        const unpack = data => data.rows.map(([id, d, u, k, m, pt, w, dk, ot, h]) => ({ id, d, i: dayIndex(d), u, k, m, pt, w, dk, ot, h }))
        this.rows = unpack(now)
        this.rowsWas = was ? unpack(was) : []
        const technics = new Map()
        for (const technic of now.technics.concat(was ? was.technics : [])) technics.set(technic.id, technic)
        this.technics = [...technics.values()].sort((a, b) => a.fullName.localeCompare(b.fullName, 'et'))
        this.workdays = now.workdays
        this.hover = null
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.loading = false
      }
    },
    buckets ([from, to]) {
      const kind = this.bucketKind
      const list = []
      for (let i = dayIndex(from); i <= dayIndex(to); i++) {
        const ymd = ymdOf(i)
        const weekday = (i + 3) % 7 // 0 = Monday
        const key = kind === 'day' ? i : kind === 'week' ? Math.floor((i + 3) / 7) : ymd.slice(0, 7)
        let last = list[list.length - 1]
        if (!last || last.key !== key) {
          last = {
            key,
            from: i,
            to: i,
            v: 0,
            weekend: kind === 'day' && weekday >= 5,
            label: kind === 'day' ? String(+ymd.slice(8, 10)) : kind === 'week' ? ymd.slice(8, 10) + '.' + ymd.slice(5, 7) : MONTHS[+ymd.slice(5, 7) - 1]
          }
          list.push(last)
        }
        last.to = i
      }
      return list
    },
    // Rows come oldest first, as the buckets do.
    fill (buckets, rows) {
      let at = 0
      for (const row of rows) {
        while (buckets[at] && row.i > buckets[at].to) at++
        if (buckets[at]) buckets[at].v += this.measure === 'n' ? 1 : row.m / 60
      }
      return buckets
    },
    niceTop (max) {
      if (max <= 0) return 4
      const power = Math.pow(10, Math.floor(Math.log10(max)))
      const lead = max / power
      return (lead <= 1 ? 1 : lead <= 2 ? 2 : lead <= 4 ? 4 : lead <= 5 ? 5 : lead <= 8 ? 8 : 10) * power
    },
    openList (list) {
      const same = this.list && this.list.kind === list.kind && this.list.id === list.id
      this.list = same ? null : list
      if (this.list) this.$nextTick(() => this.$refs.list && this.$refs.list.scrollIntoView({ behavior: 'smooth', block: 'nearest' }))
    },
    async loadList (more) {
      const query = this.listQuery
      if (!query) {
        this.entries = []
        this.listCount = 0
        return
      }
      this.listLoading = true
      try {
        const data = await api.getStatsList({ ...query, limit: PAGE, offset: more ? this.entries.length : 0 })
        if (query !== this.listQuery) return
        this.entries = more ? this.entries.concat(data.entries) : data.entries
        this.listCount = data.count
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.listLoading = false
      }
    },
    openEvent (entry) {
      this.eventId = entry.id
      this.eventChanged = false
      this.eventOpen = true
    },
    closeEvent () {
      this.eventOpen = false
      if (this.eventChanged) {
        this.load()
        this.loadList(false)
      }
    },
    // What the table shows, for Excel: semicolons and a BOM, as Estonian Excel opens it.
    exportCsv () {
      const lines = [['Tehnik', 'Töökäske', 'Tunde', 'Tööpäev', 'Nädalavahetus', 'Riigipüha', 'Vabal päeval/puhkusel']]
        .concat(this.perTechnic.map(row => [row.name, row.n, Math.round(row.m / 60), row.wd, row.we, row.ho, row.fr]))
        .concat([['Kokku', this.total.n, Math.round(this.total.m / 60), this.total.wd, this.total.we, this.total.ho, this.total.fr]])
      const csv = lines.map(line => line.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(';')).join('\r\n')
      const link = document.createElement('a')
      link.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }))
      link.download = 'statistika-' + this.span[0] + '_' + this.span[1] + '.csv'
      link.click()
      URL.revokeObjectURL(link.href)
    }
  }
}
</script>

<style scoped>
.st {
  --s-surface: #FFFFFF; --s-soft: #FAFBFA; --s-ink: #16322A; --s-ink-2: #3E504A; --s-ink-3: #5B6B66;
  --s-line: #C9D2CE; --s-line-soft: #E1E7E4; --s-green: #2E7D32; --s-blue: #1F5FD1; --s-ghost: #B9C4BF;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif; color: var(--s-ink); font-size: 14px; font-variant-numeric: tabular-nums;
  display: flex; flex-direction: column; gap: 14px; padding-bottom: 40px;
}
.st button { font: inherit; color: inherit; }
.st :focus-visible { outline: 3px solid var(--s-blue); outline-offset: 2px; }
.st h2 { font-size: 16px; font-weight: 700; font-stretch: 112%; margin: 0; }
.st-card { background: var(--s-surface); border: 1px solid var(--s-line); border-radius: 12px; }
.st-filters { padding: 4px 14px; }
.st-frow { display: flex; gap: 8px 12px; align-items: center; flex-wrap: wrap; padding: 8px 0; border-bottom: 1px solid var(--s-line-soft); }
.st-frow:last-child { border-bottom: 0; }
.st-k { flex: 0 0 118px; font-size: 11px; font-weight: 700; letter-spacing: .07em; color: var(--s-ink-3); text-transform: uppercase; }
.st-chips { display: flex; flex-wrap: wrap; gap: 6px; flex: 1 1 300px; min-width: 0; align-items: center; }
.st-chip { height: 28px; padding: 0 9px; border-radius: 6px; border: 1.5px solid var(--s-line); background: var(--s-surface); cursor: pointer; font-size: 13px; font-weight: 600; color: var(--s-ink-2); white-space: nowrap; }
.st-chip[aria-pressed="true"] { background: var(--s-ink); border-color: var(--s-ink); color: #fff; }
.st-link { border: 0; background: none; padding: 0 2px; color: var(--s-green) !important; font-weight: 600; font-size: 13px; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
.st-link:disabled { opacity: .5; cursor: default; }
.st-more { align-self: flex-start; }
.st-seg { display: inline-flex; border: 1.5px solid var(--s-line); border-radius: 6px; overflow: hidden; background: var(--s-surface); }
.st-seg button { border: 0; background: none; height: 28px; padding: 0 10px; font-size: 13px; font-weight: 600; color: var(--s-ink-2); cursor: pointer; white-space: nowrap; }
.st-seg button + button { border-left: 1.5px solid var(--s-line); }
.st-seg button[aria-pressed="true"] { background: var(--s-ink); color: #fff; }
.st-icon { width: 31px; height: 31px; border-radius: 6px; border: 1.5px solid var(--s-line); background: var(--s-surface); cursor: pointer; font-size: 18px; line-height: 1; }
.st-period { font-weight: 700; font-size: 15px; min-width: 150px; text-align: center; }
.st-out { height: 32px; padding: 0 12px; border-radius: 6px; border: 1.5px solid var(--s-line); background: var(--s-surface); font-weight: 600; font-size: 13px; cursor: pointer; }
.st-out:disabled { opacity: .5; cursor: default; }
.st-muted { color: var(--s-ink-3); font-size: 13px; }
.st-tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
.st-tile { padding: 14px 16px; text-align: left; cursor: pointer; display: flex; flex-direction: column; }
.st-tile--plain { cursor: default; }
.st-tile:not(.st-tile--plain):hover, .st-tile[aria-pressed="true"] { border-color: var(--s-ink); box-shadow: inset 0 0 0 1px var(--s-ink); }
.st-lab { font-size: 11px; font-weight: 700; letter-spacing: .07em; color: var(--s-ink-3); text-transform: uppercase; }
.st-num { font-size: 30px; font-weight: 700; font-stretch: 112%; line-height: 1.2; margin-top: 4px; }
.st-sub { font-size: 12.5px; color: var(--s-ink-3); margin-top: 2px; }
.st-delta { font-weight: 700; color: var(--s-ink-2); white-space: nowrap; }
.st-sect { padding: 14px 16px 16px; display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.st-sect-head { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; justify-content: space-between; }
.st-legend { display: flex; gap: 14px; font-size: 12.5px; color: var(--s-ink-2); align-items: center; }
.st-legend i { width: 12px; height: 12px; border-radius: 3px; display: inline-block; margin-right: 6px; vertical-align: -1px; }
.st-now { background: var(--s-green); }
.st-was { background: var(--s-ghost); }
.st-scroll { overflow-x: auto; }
.st-chart { position: relative; min-width: 640px; }
.st-chart svg { display: block; }
.st-chart text { fill: var(--s-ink-3); font-size: 11px; }
.st-tip { position: absolute; pointer-events: none; background: var(--s-ink); color: #fff; border-radius: 6px; padding: 6px 9px; font-size: 12.5px; line-height: 1.4; white-space: nowrap; transform: translate(-50%, -100%); }
.st-table { border-collapse: collapse; width: 100%; min-width: 760px; }
.st-table--narrow { min-width: 420px; }
.st-table th, .st-table td { padding: 9px 10px; text-align: right; border-bottom: 1px solid var(--s-line-soft); white-space: nowrap; }
.st-table th:first-child, .st-table td:first-child { text-align: left; }
.st-table thead th { font-size: 11px; font-weight: 700; letter-spacing: .06em; color: var(--s-ink-3); text-transform: uppercase; border-bottom: 1px solid var(--s-line); vertical-align: bottom; }
.st-table:not(.st-table--narrow):not(.st-table--list) thead th { cursor: pointer; }
.st-table thead th[aria-sort] { color: var(--s-ink); }
.st-table tbody td:first-child { font-weight: 600; }
.st-table tfoot td { font-weight: 700; background: var(--s-soft); border-bottom: 0; }
.st-table--list { min-width: 980px; }
.st-table--list th, .st-table--list td { text-align: left; }
.st-table--list tbody td:first-child { font-weight: 400; }
.st-table--list td:nth-child(8), .st-table--list td:nth-child(9) { white-space: normal; min-width: 150px; }
.st-table--list a { color: var(--s-ink); text-decoration: underline; text-decoration-color: var(--s-line); text-underline-offset: 3px; }
.st-bar { display: inline-block; height: 8px; border-radius: 4px; background: var(--s-green); vertical-align: middle; margin-right: 8px; }
.st-numlink { border: 0; background: none; padding: 0; cursor: pointer; text-decoration: underline; text-decoration-color: var(--s-line); text-underline-offset: 3px; }
.st-numlink:hover { text-decoration-color: var(--s-ink); }
.st-two { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: 14px; }
.st-pay { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.st-pay div { border: 1px solid var(--s-line-soft); border-radius: 8px; padding: 10px 12px; }
.st-pay b { display: block; font-size: 20px; font-stretch: 112%; }
@media (max-width: 860px) {
  .st-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .st-two { grid-template-columns: minmax(0, 1fr); }
  .st-k { flex-basis: 100%; }
  .st-num { font-size: 24px; }
}
</style>
